import * as React from "react";

import { Box, Divider, Link, Typography } from "@material-ui/core";

import { SalesChannel } from "client/lib/apt-merchant";
import { L10n } from "client/utils/branding";
import { Translate } from "client/utils/internationalization";

import { useStyles } from "./styles";
import { FAQ } from "./types";

export const getInStoreFaqs = (
  branding: L10n,
  salesChannel: SalesChannel,
  t: Translate,
  classes: ReturnType<typeof useStyles>
): FAQ[] => {
  const { brandTitle, brandUrl } = branding;

  let helpUrl = "";

  if (brandTitle === "Afterpay") {
    helpUrl = "https://help.afterpay.com";
  } else if (brandTitle === "Clearpay") {
    helpUrl = "https://help.clearpay.co.uk/";
  }

  const inStoreBaseFaqs: FAQ[] = [
    {
      question: t("uiFaqInStoreHowDoesInStoreWorkQuestion", { brandTitle }),
      answer: (
        <Box>
          <ol>
            <li>
              <Typography>
                {t("uiFaqInStoreHowDoesInStoreWorkAnswer1", { brandTitle })}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreHowDoesInStoreWorkAnswer2", { brandTitle })}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreHowDoesInStoreWorkAnswer3", { brandTitle })}
              </Typography>
            </li>
          </ol>
        </Box>
      ),
    },
    {
      question: t("uiFaqInStoreCustomerSetupQuestion", { brandTitle }),
      answer: (
        <Box>
          <ol>
            <li>
              <Typography>
                {t("uiFaqInStoreCustomerSetupAnswer1", { brandTitle })}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreCustomerSetupAnswer2", { brandTitle })}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreCustomerSetupAnswer3")}
                <strong>{t("uiFaqInStoreCustomerSetupAnswer4")}</strong>
                {t("uiFaqInStoreCustomerSetupAnswer5", { brandTitle })}
              </Typography>
            </li>
          </ol>
        </Box>
      ),
    },
    {
      question: t("uiFaqInStoreCustomerPayInStoreQuestion", { brandTitle }),
      answer: (
        <Box>
          <ol>
            <li>
              <Typography>
                {t("uiFaqInStoreCustomerPayInStoreAnswer1", { brandTitle })}
                <strong>{t("uiFaqInStoreCustomerPayInStoreAnswer2")}</strong>
                {t("uiFaqInStoreCustomerPayInStoreAnswer3")}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreCustomerPayInStoreAnswer4", { brandTitle })}
                <strong>{t("uiFaqInStoreCustomerPayInStoreAnswer5")}</strong>
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreCustomerPayInStoreAnswer6")}
              </Typography>
            </li>
          </ol>

          <Typography>
            {t("uiFaqInStoreCustomerPayInStoreAnswer7")}{" "}
            <Link
              href={"https://afterpay.com/installment-agreement"}
              rel="noopener"
              target="_blank"
            >
              {t("uiFaqInStoreCustomerPayInStoreAnswer8", { brandUrl })}
            </Link>
          </Typography>
        </Box>
      ),
    },
    {
      question: t("uiFaqInStoreRefundsAndReturnsQuestion", { brandTitle }),
      answer: (
        <Box>
          <Typography>
            {t("uiFaqInStoreRefundsAndReturnsAnswer1", { brandTitle })}
          </Typography>
          <ol>
            <li>
              <Typography>
                {t("uiFaqInStoreRefundsAndReturnsAnswer2")}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreRefundsAndReturnsAnswer3")}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreRefundsAndReturnsAnswer4", { brandTitle })}
              </Typography>
            </li>
          </ol>

          <Typography>{t("uiFaqInStoreRefundsAndReturnsAnswer5")}</Typography>
          <ol>
            <li>
              <Typography>
                {t("uiFaqInStoreRefundsAndReturnsAnswer6")}
                <strong>{t("uiFaqInStoreRefundsAndReturnsAnswer7")}</strong>
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreRefundsAndReturnsAnswer8")}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreRefundsAndReturnsAnswer9")}
                <strong>{t("uiFaqInStoreRefundsAndReturnsAnswer10")}</strong>
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreRefundsAndReturnsAnswer11")}
              </Typography>
            </li>
          </ol>

          <Typography className={classes.paragraph}>
            {t("uiFaqInStoreRefundsAndReturnsAnswer12")}
            <strong>{t("uiFaqInStoreRefundsAndReturnsAnswer13")}</strong>
          </Typography>
          <Typography className={classes.paragraph}>
            {t("uiFaqInStoreRefundsAndReturnsAnswer14", { brandTitle })}
          </Typography>
          <Typography>{t("uiFaqInStoreRefundsAndReturnsAnswer15")}</Typography>
          <Box
            display="flex"
            flexDirection="column"
            border={1}
            borderRadius={"0.75rem"}
          >
            <Box display="flex" padding={"1.25rem"}>
              <Box mr={"2.5rem"}>For iPhone</Box>
              <Box>
                <ol style={{ marginTop: "0px" }}>
                  <li>
                    <Typography>
                      Open <strong>Settings</strong>
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Scroll to <strong>Wallet & Apple Pay</strong>
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Tap the <strong>Afterpay Card</strong> to reveal the last
                      4 digits.
                    </Typography>
                  </li>
                </ol>
              </Box>
            </Box>
            <Divider />
            <Box display="flex" padding={"1.25rem"}>
              <Box mr={"2.5rem"}>For Android</Box>
              <Box>
                <ol style={{ marginTop: "0px" }}>
                  <li>
                    <Typography>
                      Open <strong>Google Pay</strong>
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Go to <strong>Payment</strong>
                    </Typography>
                  </li>
                  <li>
                    <Typography>
                      Tap the card number, then scroll to{" "}
                      <strong>Virtual Card Number.</strong>
                    </Typography>
                  </li>
                </ol>
              </Box>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      question: t("uiFaqInStoreCustomerIssuesQuestion", { brandTitle }),
      answer: (
        <Box>
          <Typography>
            {t("uiFaqInStoreCustomerIssuesAnswer1", { brandTitle })}
          </Typography>
          <ul>
            <li>{t("uiFaqInStoreCustomerIssuesAnswer2", { brandTitle })}</li>
            <li>{t("uiFaqInStoreCustomerIssuesAnswer3", { brandTitle })}</li>
            <li>{t("uiFaqInStoreCustomerIssuesAnswer4", { brandTitle })}</li>
            <li>{t("uiFaqInStoreCustomerIssuesAnswer5", { brandTitle })}</li>
          </ul>

          <Typography>
            {t("uiFaqInStoreCustomerIssuesAnswer6", { brandTitle })}
          </Typography>
          <ul>
            <li>
              <strong>{t("uiFaqInStoreCustomerIssuesAnswer7")}</strong>
              {t("uiFaqInStoreCustomerIssuesAnswer8")}
            </li>
            <li>
              <strong>
                {t("uiFaqInStoreCustomerIssuesAnswer9", { brandTitle })}
              </strong>
              {t("uiFaqInStoreCustomerIssuesAnswer10", { helpUrl })}
            </li>
            <li>
              <strong>{t("uiFaqInStoreCustomerIssuesAnswer11")}</strong>
              {t("uiFaqInStoreCustomerIssuesAnswer12")}
            </li>
          </ul>
        </Box>
      ),
    },
    {
      question: t("uiFaqInStoreSplitPaymentQuestion", { brandTitle }),
      answer: (
        <Box>
          <Typography>
            {t("uiFaqInStoreSplitPaymentAnswer1", { brandTitle })}
          </Typography>
          <ol>
            <li>
              <Typography>{t("uiFaqInStoreSplitPaymentAnswer2")}</Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreSplitPaymentAnswer3", { brandTitle })}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreSplitPaymentAnswer4", { brandTitle })}
                <strong>
                  {t("uiFaqInStoreSplitPaymentAnswer5", { brandTitle })}
                </strong>
                {t("uiFaqInStoreSplitPaymentAnswer6")}
              </Typography>
            </li>
            <li>
              <Typography>{t("uiFaqInStoreSplitPaymentAnswer7")}</Typography>
            </li>
          </ol>
        </Box>
      ),
    },
    {
      question: t("uiFaqInStoreSplitPaymentRefundsQuestion", { brandTitle }),
      answer: (
        <Box>
          <ol>
            <li>
              <Typography>
                {t("uiFaqInStoreSplitPaymentRefundsAnswer1")}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreSplitPaymentRefundsAnswer2")}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreSplitPaymentRefundsAnswer3", { brandTitle })}
                <strong>
                  {t("uiFaqInStoreSplitPaymentRefundsAnswer4", { brandTitle })}
                </strong>
                {t("uiFaqInStoreSplitPaymentRefundsAnswer5")}
              </Typography>
            </li>
            <li>
              <Typography>
                {t("uiFaqInStoreSplitPaymentRefundsAnswer6", { brandTitle })}
              </Typography>
            </li>
          </ol>
        </Box>
      ),
    },
    {
      question: t("uiFaqInStoreProhibitedTermsHeader"),
      answer: (
        <Box>
          <Typography>
            {t("uiFaqInStoreProhibitedTerms1", { brandTitle })}
          </Typography>
          <ul>
            <li>{t("uiFaqInStoreProhibitedTerms2")}</li>
            <li>{t("uiFaqInStoreProhibitedTerms3")}</li>
            <li>{t("uiFaqInStoreProhibitedTerms4")}</li>
            <li>{t("uiFaqInStoreProhibitedTerms5")}</li>
            <li>{t("uiFaqInStoreProhibitedTerms6")}</li>
            <li>{t("uiFaqInStoreProhibitedTerms7")}</li>
            <li>{t("uiFaqInStoreProhibitedTerms8")}</li>
            <li>{t("uiFaqInStoreProhibitedTerms9")}</li>
            <li>{t("uiFaqInStoreProhibitedTerms10")}</li>
          </ul>
        </Box>
      ),
    },
  ];

  const inStoreOnlyFAQs: FAQ[] = [
    {
      question: t("uiFaqInStoreWhatIsAfterpayQuestion", { brandTitle }),
      answer: (
        <Box>
          <Typography>
            {t("uiFaqInStoreWhatIsAfterpayAnswer1", { brandTitle })}
          </Typography>
          <Typography>
            {t("uiFaqInStoreWhatIsAfterpayAnswer2", { brandTitle })}
            <Link href={brandUrl} rel="noopener" target="_blank">
              {brandUrl}
            </Link>
          </Typography>
        </Box>
      ),
    },
    {
      question: t("uiFaqInStoreLateFeeQuestion", { brandTitle }),
      answer: (
        <Box>
          <Typography>
            {t("uiFaqInStoreLateFeeAnswer1", { brandTitle })}
            <Link
              href={t("uiFaqInStoreLateFeeLinkHref", { brandUrl })}
              rel="noopener"
              target="_blank"
            >
              {t("uiFaqInStoreLateFeeAnswer2", { brandUrl })}
            </Link>
          </Typography>
        </Box>
      ),
    },
    {
      question: t("uiFaqInStoreWhoCanSignUpQuestion", { brandTitle }),
      answer: (
        <Box>
          <Typography>
            {t("uiFaqInStoreWhoCanSignUpAnswer1", { brandTitle })}
          </Typography>
          <ul>
            <li>{t("uiFaqInStoreWhoCanSignUpAnswer2")}</li>
            <li>{t("uiFaqInStoreWhoCanSignUpAnswer3")}</li>
            <li>{t("uiFaqInStoreWhoCanSignUpAnswer4")}</li>
            <li>{t("uiFaqInStoreWhoCanSignUpAnswer5")}</li>
            <li>{t("uiFaqInStoreWhoCanSignUpAnswer6")}</li>
          </ul>
        </Box>
      ),
    },
  ];

  if (salesChannel === "In-store Only") {
    return [...inStoreOnlyFAQs, ...inStoreBaseFaqs];
  }
  return inStoreBaseFaqs;
};
